import React, { useState, useEffect, useRef } from 'react';
import Field from './ui/Field';
import Appear from './ui/Appear';
import Reveal from './ui/Reveal';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const launchIcon = new L.Icon({
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
  className: 'launch-marker',
});

const audienceIcon = new L.Icon({
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
  className: 'audience-marker',
});

const MapClickHandler = ({ onMapClick }) => {
  useMapEvents({ click: (e) => onMapClick(e.latlng) });
  return null;
};

const FlyTo = ({ lat, lng, zoom }) => {
  const map = useMap();
  const lastRef = useRef('');
  useEffect(() => {
    const key = `${lat},${lng}`;
    if (lat && lng && key !== lastRef.current) {
      lastRef.current = key;
      map.flyTo([lat, lng], zoom || 14);
    }
  }, [lat, lng, zoom, map]);
  return null;
};

const LocationPicker = ({
  location, onChange,
  launchLat, launchLng, onLaunchChange,
  audienceLat, audienceLng, onAudienceChange,
  showAudience,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [pinMode, setPinMode] = useState('launch'); // 'launch' or 'audience'
  const [flyTarget, setFlyTarget] = useState(null);
  const searchRef = useRef(null);
  const timerRef = useRef(null);

  // Determine map center
  const centerLat = launchLat || 39.8283;
  const centerLng = launchLng || -98.5795;
  const hasAnyPin = !!(launchLat || audienceLat);

  useEffect(() => {
    const handleClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowResults(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const searchLocation = async (q) => {
    if (!q || q.length < 3) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=5&countrycodes=us`, {
        headers: { 'Accept-Language': 'en' },
      });
      const data = await res.json();
      setResults(data);
      setShowResults(true);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleQueryChange = (val) => {
    setQuery(val);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => searchLocation(val), 400);
  };

  const selectResult = (result) => {
    onChange(result.display_name);
    setQuery(result.display_name);
    setShowResults(false);
    setFlyTarget({ lat: parseFloat(result.lat), lng: parseFloat(result.lon) });
  };

  const handleMapClick = (latlng) => {
    if (pinMode === 'launch') {
      onLaunchChange(latlng.lat, latlng.lng);
      if (!location) onChange(`${latlng.lat.toFixed(4)}, ${latlng.lng.toFixed(4)}`);
    } else if (pinMode === 'audience' && showAudience) {
      onAudienceChange(latlng.lat, latlng.lng);
    }
  };

  // Crowd distance calc
  let crowdDist = null;
  if (launchLat && audienceLat) {
    const R = 20902231; // Earth radius in feet
    const dLat = (audienceLat - launchLat) * Math.PI / 180;
    const dLng = (audienceLng - launchLng) * Math.PI / 180;
    const a = Math.sin(dLat/2)**2 + Math.cos(launchLat * Math.PI/180) * Math.cos(audienceLat * Math.PI/180) * Math.sin(dLng/2)**2;
    crowdDist = Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)));
  }

  return (
    <div className="space-y-2">
      {/* Search */}
      <div ref={searchRef} className="relative z-[1001]">
        <label className="text-xs text-on-surface-variant">Location</label>
        <Field
          type="text"
          value={query || location || ''}
          onChange={(e) => handleQueryChange(e.target.value)}
          placeholder="Search for a location..."
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              clearTimeout(timerRef.current);
              searchLocation(query || location || '');
            }
          }}
        />
        <Appear as="div" show={!!searching} className="absolute right-3 top-8 text-xs text-on-surface-variant">Searching...</Appear>
        <Appear as="div" show={!!(showResults && results.length > 0)} className="absolute z-[1002] w-full mt-1 bg-surface-container-high rounded-md shadow-overlay max-h-48 overflow-y-auto">
            {results.map((r, i) => (
              <button key={i} onClick={() => selectResult(r)}
                className="w-full text-left px-3 py-2 text-sm text-on-surface hover:bg-gray-700 hover:text-white transition-colors border-b border-gray-700/50 last:border-0">
                {r.display_name}
              </button>
            ))}
        </Appear>
      </div>

      {/* Pin mode buttons */}
      <div className="flex gap-2">
        <button type="button" onClick={() => setPinMode('launch')}
          className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
            pinMode === 'launch'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-on-surface-variant hover:text-white'
          }`}>
          Drop Launch Pin
        </button>
        {showAudience && (
          <button type="button" onClick={() => setPinMode('audience')}
            className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
              pinMode === 'audience'
                ? 'bg-green-600 text-white'
                : 'bg-gray-700 text-on-surface-variant hover:text-white'
            }`}>
            Drop Audience Pin
          </button>
        )}
        <span className="text-xs text-on-surface-variant self-center ml-1">
          Click map to place {pinMode === 'launch' ? 'launch site' : 'audience'} pin
        </span>
      </div>

      {/* Map */}
      <div className="rounded overflow-hidden border border-gray-700" style={{ height: 280 }}>
        <MapContainer center={[centerLat, centerLng]} zoom={hasAnyPin ? 14 : 4} style={{ height: '100%', width: '100%' }} attributionControl={false}>
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            attribution='&copy; Esri, Maxar, Earthstar Geographics'
          />
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
            attribution='&copy; Stamen Design'
          />
          <MapClickHandler onMapClick={handleMapClick} />
          {launchLat && launchLng && <Marker position={[launchLat, launchLng]} icon={launchIcon} />}
          {showAudience && audienceLat && audienceLng && <Marker position={[audienceLat, audienceLng]} icon={audienceIcon} />}
          {flyTarget && <FlyTo lat={flyTarget.lat} lng={flyTarget.lng} zoom={14} />}
          {!flyTarget && launchLat && <FlyTo lat={launchLat} lng={launchLng} zoom={14} />}
        </MapContainer>
      </div>

      {/* Coordinate readout */}
      <Reveal show={!!(launchLat || (showAudience && audienceLat) || crowdDist)}>
        <div className="flex gap-4 text-xs">
          <Appear show={!!launchLat} className="text-blue-400">
            Launch: {launchLat?.toFixed(5)}, {launchLng?.toFixed(5)}
          </Appear>
          <Appear show={!!(showAudience && audienceLat)} className="text-green-400">
            Audience: {audienceLat?.toFixed(5)}, {audienceLng?.toFixed(5)}
          </Appear>
          <Appear show={!!crowdDist} className="text-on-surface-variant">
            ~{crowdDist?.toLocaleString()} ft apart
          </Appear>
        </div>
      </Reveal>

      {/* Marker color CSS overrides */}
      <style>{`
        .launch-marker { filter: hue-rotate(0deg); }
        .audience-marker { filter: hue-rotate(100deg) brightness(1.2); }
      `}</style>
    </div>
  );
};

export default LocationPicker;
